import type { ResultSet } from '@projections/engine';
import { useMemo } from 'react';
import { formatCompactCurrency } from '../../design/format';
import { describePath, type PathShortcutKind, pathForShortcut } from './ledgerView';

/**
 * PATH PICKER (PLAN.md M7 screen 3 — "pick a percentile/path"). Lets the user choose WHICH path's
 * ledger to read: by percentile shortcut (P5 / P25 / Median / P75 / P95 — mapped to the path whose
 * terminal wealth sits at that quantile of all paths) or by raw path index. A "Worst" shortcut
 * jumps to the first depleted path (else the poorest), the natural place to debug a failure.
 *
 * The percentile→path mapping ranks paths by terminal `endBalance` (pure helpers in `ledgerView`),
 * so "Median" is a CONCRETE path whose full year-by-year ledger can then be read — not an
 * interpolated aggregate. The chosen path index feeds the ledger table.
 */
export interface PathPickerProps {
  result: ResultSet;
  pathIndex: number;
  onPick: (pathIndex: number) => void;
}

const SHORTCUTS: ReadonlyArray<{ kind: PathShortcutKind; label: string }> = [
  { kind: 'worst', label: 'Worst' },
  { kind: 'p5', label: 'P5' },
  { kind: 'p25', label: 'P25' },
  { kind: 'median', label: 'Median' },
  { kind: 'p75', label: 'P75' },
  { kind: 'p95', label: 'P95' },
];

export function PathPicker({ result, pathIndex, onPick }: PathPickerProps): React.ReactElement {
  const count = result.ledgers.length;
  // Resolve each shortcut to its concrete path index once per result, so we can highlight the
  // chip matching the active path and show what it selects.
  const shortcutIndices = useMemo(
    () => SHORTCUTS.map((s) => ({ ...s, index: pathForShortcut(result, s.kind) })),
    [result],
  );
  const active = useMemo(() => describePath(result, pathIndex), [result, pathIndex]);

  const clamp = (n: number): number => Math.min(Math.max(0, n), Math.max(0, count - 1));

  return (
    <div className="ledger__picker">
      <div
        className="ledger__picker-shortcuts"
        role="toolbar"
        aria-label="Pick a path by percentile"
      >
        {shortcutIndices.map((s) => (
          <button
            key={s.kind}
            type="button"
            className={`ledger__chip${s.index === pathIndex ? ' ledger__chip--active' : ''}${
              s.kind === 'worst' ? ' ledger__chip--worst' : ''
            }`}
            onClick={() => onPick(s.index)}
            aria-pressed={s.index === pathIndex}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="ledger__picker-index">
        <label className="field__label" htmlFor="path-index">
          Path
        </label>
        <input
          id="path-index"
          className="input input--num"
          type="number"
          min={0}
          max={Math.max(0, count - 1)}
          value={pathIndex}
          onChange={(e) => onPick(clamp(Math.trunc(Number(e.currentTarget.value))))}
          style={{ width: 96 }}
        />
        <span className="ledger__picker-of">of {count.toLocaleString('en-US')}</span>
      </div>

      <div
        className={`ledger__picker-summary${active.depleted ? ' ledger__picker-summary--neg' : ''}`}
      >
        <span className="ledger__picker-summary-label">Terminal</span>
        <span className="num">{formatCompactCurrency(active.terminalWealth)}</span>
        {active.depleted && <span className="badge badge--neg">depleted</span>}
      </div>
    </div>
  );
}
